import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import API_URL from '../../config'
import { setAuthToken } from '../../services/auth'

const PlaylistList = () => {
  // State for search criteria
  const [search, setSearch] = useState('')
  const [service, setService] = useState('')
  const [sortBy, setSortBy] = useState('title')
  const [sortOrder, setSortOrder] = useState('asc')

  // State for playlists
  const [playlists, setPlaylists] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Services options
  const serviceOptions = [
    { value: '', label: 'All Services' },
    { value: 'spotify', label: 'Spotify' },
    { value: 'apple', label: 'Apple Music' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'bandcamp', label: 'Bandcamp' },
  ]

  // Sort options
  const sortOptions = [
    { value: 'title', label: 'Title' },
    { value: 'songCount', label: 'Song Count' },
    { value: 'userCount', label: 'User Count' },
  ]

  // Function to fetch playlists
  const fetchPlaylists = async () => {
    try {
      setLoading(true)
      setError(null)

      // Set auth token for the request
      const token = localStorage.getItem('token')
      console.log(
        'Playlist API - Token from localStorage:',
        token ? `${token.substring(0, 20)}...` : 'No token found'
      )
      setAuthToken(token)
      console.log(
        'Playlist API - Authorization header:',
        axios.defaults.headers.common['Authorization'] ? 'Set' : 'Not set'
      )

      // Build params
      const params = {
        search,
        service,
        sortBy,
        sortOrder,
        page,
        pageSize,
      }

      // Make API request with explicit token in headers
      const response = await axios.get(`${API_URL}admin/playlists`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      // Update state with results
      setPlaylists(response.data.playlists)
      setTotalPages(response.data.pagination.totalPages)
      setTotalResults(response.data.pagination.totalResults)
    } catch (err) {
      console.error('Error fetching playlists:', err)
      setError(`Failed to fetch playlists: ${err.response?.data?.error || err.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Function to handle pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  // Function to handle search form submission
  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1) // Reset to first page
    fetchPlaylists()
  }

  // Function to reset search
  const resetSearch = () => {
    setSearch('')
    setService('')
    setSortBy('title')
    setSortOrder('asc')
    setPage(1)
  }

  // Function to handle sort change
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Default to ascending for new field
      setSortBy(newSortBy)
      setSortOrder('asc')
    }
  }

  // Fetch playlists on mount and when search parameters change
  useEffect(() => {
    fetchPlaylists()
  }, [page, pageSize, sortBy, sortOrder])

  // Function to render sort indicator
  const renderSortIndicator = (field) => {
    if (sortBy !== field) return null
    return sortOrder === 'asc' ? ' ↑' : ' ↓'
  }

  return (
    <div>
      <div className='mb-6 flex items-center justify-between'>
        <h2 className='text-xl font-semibold'>Playlists</h2>
        <Link
          to='/admin/playlist/create'
          className='bg-blue-500 hover:bg-blue-600 text-white rounded px-4 py-2'
        >
          Create New Playlist
        </Link>
      </div>

      {/* Search Form */}
      <div className='mb-6'>
        <form onSubmit={handleSearch} className='space-y-4'>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-4'>
            <div>
              <label className='mb-1 block'>Search</label>
              <input
                type='text'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search by title...'
                className='w-full rounded border p-2'
              />
            </div>

            <div>
              <label className='mb-1 block'>Service</label>
              <select
                value={service}
                onChange={(e) => setService(e.target.value)}
                className='w-full rounded border p-2'
              >
                {serviceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='mb-1 block'>Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className='w-full rounded border p-2'
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='mb-1 block'>Sort Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className='w-full rounded border p-2'
              >
                <option value='asc'>Ascending</option>
                <option value='desc'>Descending</option>
              </select>
            </div>
          </div>

          <div className='flex space-x-2'>
            <button
              type='submit'
              className='bg-blue-500 hover:bg-blue-600 text-white rounded px-4 py-2'
            >
              Search
            </button>
            <button
              type='button'
              onClick={() => {
                resetSearch()
                fetchPlaylists()
              }}
              className='bg-gray-200 hover:bg-gray-300 rounded px-4 py-2'
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Results */}
      <div className='mb-6 overflow-x-auto'>
        {loading ? (
          <div className='p-4 text-center'>Loading playlists...</div>
        ) : error ? (
          <div className='bg-red-100 text-red-700 rounded p-4'>{error}</div>
        ) : playlists.length > 0 ? (
          <>
            <div className='mb-2'>Found {totalResults} playlist(s)</div>
            <table className='min-w-full border'>
              <thead>
                <tr className='bg-gray-100'>
                  <th
                    className='cursor-pointer border px-4 py-2'
                    onClick={() => handleSortChange('title')}
                  >
                    Title {renderSortIndicator('title')}
                  </th>
                  <th className='border px-4 py-2'>Artists</th>
                  <th className='border px-4 py-2'>Service</th>
                  <th
                    className='cursor-pointer border px-4 py-2'
                    onClick={() => handleSortChange('songCount')}
                  >
                    Songs {renderSortIndicator('songCount')}
                  </th>
                  <th
                    className='cursor-pointer border px-4 py-2'
                    onClick={() => handleSortChange('userCount')}
                  >
                    Users {renderSortIndicator('userCount')}
                  </th>
                  <th className='border px-4 py-2'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {playlists.map((playlist) => (
                  <tr key={playlist.id} className='hover:bg-gray-50'>
                    <td className='border px-4 py-2'>{playlist.title}</td>
                    <td className='border px-4 py-2'>{playlist.artists.join(', ')}</td>
                    <td className='border px-4 py-2'>{playlist.service}</td>
                    <td className='border px-4 py-2'>{playlist.songCount}</td>
                    <td className='border px-4 py-2'>{playlist.userCount}</td>
                    <td className='border px-4 py-2'>
                      <Link
                        to={`/admin/playlist/edit/${playlist.id}`}
                        className='bg-blue-500 text-white hover:bg-blue-600 rounded px-2 py-1'
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className='mt-4 flex justify-center'>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className={`mx-1 rounded px-3 py-1 ${
                  page === 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                &laquo; Previous
              </button>

              <span className='mx-2 px-3 py-1'>
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                className={`mx-1 rounded px-3 py-1 ${
                  page === totalPages
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Next &raquo;
              </button>
            </div>
          </>
        ) : (
          <div className='border p-4 text-center'>
            No playlists found. Try adjusting your search criteria.
          </div>
        )}
      </div>
    </div>
  )
}

export default PlaylistList
